import { baseApiAuthenticated } from '@/api/v2/index';
import { urlBuilder } from '@/api/utils';
import {
    ConditionLink,
    convertConditionLinkToBackend,
    convertConditionLinkToFrontend,
    ImportedConditionLink,
    PostImportConditionLinkDTO,
} from '@/models/conditionLink';
import { BackendType } from '@/models/general';

type GetConditionLinksResponse = {
    originalConditionLinks: ConditionLink<BackendType>[];
};

type GetImportedConditionLinksResponse = {
    importedConditionLinks: ImportedConditionLink[];
};

const baseUrl = {
    original: urlBuilder('liftoperators', 'original-condition-links'),
    import: urlBuilder('liftoperators', 'imported-condition-links'),
};

export const conditionLinksApi = baseApiAuthenticated
    .enhanceEndpoints({
        addTagTypes: ['CONDITION_LINKS', 'IMPORTED_CONDITION_LINKS'],
    })
    .injectEndpoints({
        endpoints: (builder) => ({
            getImportedConditionLinks: builder.query<
                ImportedConditionLink[],
                { liftOperatorId: string }
            >({
                query: ({ liftOperatorId }) =>
                    `${baseUrl.import}/${liftOperatorId}`,
                transformResponse: (
                    response: GetImportedConditionLinksResponse
                ) => response.importedConditionLinks,
                providesTags: ['IMPORTED_CONDITION_LINKS'],
            }),

            getImportedConditionLink: builder.query<
                ImportedConditionLink,
                { liftOperatorId: string; id: string }
            >({
                query: ({ liftOperatorId, id }) =>
                    `${baseUrl.import}/${liftOperatorId}/${id}`,

                providesTags: ['IMPORTED_CONDITION_LINKS'],
            }),

            putImportedConditionLink: builder.mutation<
                ImportedConditionLink,
                {
                    liftOperatorId: string;
                    importedConditionLink: ImportedConditionLink;
                }
            >({
                query: ({ liftOperatorId, importedConditionLink }) => ({
                    method: 'PUT',
                    url: `${baseUrl.import}/${liftOperatorId}/${importedConditionLink.id}`,
                    body: importedConditionLink,
                }),
                invalidatesTags: ['IMPORTED_CONDITION_LINKS'],
            }),

            postImportedConditionLink: builder.mutation<
                ConditionLink,
                {
                    liftOperatorId: string;
                    importConditionLink: PostImportConditionLinkDTO;
                }
            >({
                query: ({ liftOperatorId, importConditionLink }) => ({
                    method: 'POST',
                    url: `${baseUrl.import}/${liftOperatorId}`,
                    body: importConditionLink,
                }),
                invalidatesTags: ['IMPORTED_CONDITION_LINKS'],
            }),

            deleteImportedConditionLink: builder.mutation<
                void,
                {
                    liftOperatorId: string;
                    id: string;
                }
            >({
                query: ({ liftOperatorId, id }) => ({
                    method: 'DELETE',
                    url: `${baseUrl.import}/${liftOperatorId}/${id}`,
                }),
                invalidatesTags: ['IMPORTED_CONDITION_LINKS'],
            }),

            getConditionLinksImport: builder.query<
                ConditionLink,
                { importId: string }
            >({
                query: ({ importId }) =>
                    `${baseUrl.original}/import/${importId}`,
                transformResponse: (importLink: ConditionLink<BackendType>) => {
                    return convertConditionLinkToFrontend(importLink);
                },
            }),

            getConditionLinks: builder.query<
                ConditionLink[],
                { liftOperatorId: string }
            >({
                query: ({ liftOperatorId }) =>
                    `${baseUrl.original}/${liftOperatorId}`,
                transformResponse: ({
                    originalConditionLinks,
                }: GetConditionLinksResponse) => {
                    return originalConditionLinks.map(
                        convertConditionLinkToFrontend
                    );
                },
                providesTags: ['CONDITION_LINKS'],
            }),

            getConditionLink: builder.query<
                ConditionLink,
                { liftOperatorId: string; id: string }
            >({
                query: ({ liftOperatorId, id }) =>
                    `${baseUrl.original}/${liftOperatorId}/${id}`,
                transformResponse: (response: ConditionLink<BackendType>) => {
                    return convertConditionLinkToFrontend(response);
                },
                providesTags: ['CONDITION_LINKS'],
            }),

            putConditionLink: builder.mutation<
                ConditionLink,
                {
                    liftOperatorId: string;
                    conditionLink: ConditionLink;
                }
            >({
                query: ({ liftOperatorId, conditionLink }) => ({
                    method: 'PUT',
                    url: `${baseUrl.original}/${liftOperatorId}/${conditionLink.id}`,
                    body: convertConditionLinkToBackend(conditionLink),
                }),
                transformResponse: (response: ConditionLink<BackendType>) => {
                    return convertConditionLinkToFrontend(response);
                },
                invalidatesTags: [
                    'CONDITION_LINKS',
                    'IMPORTED_CONDITION_LINKS',
                ],
            }),

            postConditionLink: builder.mutation<
                ConditionLink,
                {
                    liftOperatorId: string;
                    conditionLink: ConditionLink;
                }
            >({
                query: ({ liftOperatorId, conditionLink }) => ({
                    method: 'POST',
                    url: `${baseUrl.original}/${liftOperatorId}`,
                    body: convertConditionLinkToBackend(conditionLink),
                }),
                transformResponse: (response: ConditionLink<BackendType>) => {
                    return convertConditionLinkToFrontend(response);
                },
                invalidatesTags: [
                    'CONDITION_LINKS',
                    'IMPORTED_CONDITION_LINKS',
                ],
            }),

            deleteConditionLink: builder.mutation<
                void,
                {
                    liftOperatorId: string;
                    id: string;
                }
            >({
                query: ({ liftOperatorId, id }) => ({
                    method: 'DELETE',
                    url: `${baseUrl.original}/${liftOperatorId}/${id}`,
                }),
                invalidatesTags: [
                    'CONDITION_LINKS',
                    'IMPORTED_CONDITION_LINKS',
                ],
            }),
        }),
    });

export const {
    useGetImportedConditionLinksQuery,
    usePutImportedConditionLinkMutation,
    useDeleteImportedConditionLinkMutation,
    useGetConditionLinksQuery,
    useGetConditionLinkQuery,
    useGetImportedConditionLinkQuery,
    usePutConditionLinkMutation,
    usePostConditionLinkMutation,
    useDeleteConditionLinkMutation,
    useLazyGetConditionLinksImportQuery,
    usePostImportedConditionLinkMutation,
} = conditionLinksApi;

type UsePostOrPutConditionLinkMutation = {
    liftOperatorId: string;
    conditionLink: ConditionLink;
};

export const usePostOrPutConditionLinkMutation = (
    params: UsePostOrPutConditionLinkMutation
): ReturnType<
    typeof usePostConditionLinkMutation | typeof usePutConditionLinkMutation
> => {
    const [postConditionLink, postOptions] = usePostConditionLinkMutation();
    const [putConditionLink, putOptions] = usePutConditionLinkMutation();

    return !params.conditionLink.id
        ? [postConditionLink, postOptions]
        : [putConditionLink, putOptions];
};
